import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AppConfig, appConfigService } from '@app/config';
import { JedinicaGradiva } from '@app/shared/models/jedinica-gradiva';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  BusinessItem,
  HttpMethod,
  HttpService,
  ItemViewProviderDirective
} from '@ntap/core';
import { PrimeNGInputTextComponent } from '@ntap/primeng';
import { Message } from 'primeng/api';
import { InputNumberInputEvent } from 'primeng/inputnumber';
import { debounceTime, first, Subscription, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-arhivska-oznaka',
  templateUrl: './arhivska-oznaka.html',
  styleUrls: ['./arhivska-oznaka.scss']
})
export class ArhivskaOznakaComponent
  extends PrimeNGInputTextComponent
  implements OnInit, OnDestroy
{
  public prefixValue = '';
  public orderFormControl: FormControl;
  public warnMessage: Message[];
  public itemViewProvider = inject(ItemViewProviderDirective);

  constructor(
    formService: DynamicFormService,
    attributeStateService: AttributeStateService,
    @Inject(appConfigService) private config: AppConfig,
    private httpService: HttpService
  ) {
    super(formService, attributeStateService);
    this.config = config;
    this.httpService = httpService;
  }

  private baseUrl = this.config.apiHost;
  private valueChangeSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.valueChangeSubscription = this.formControl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => this.checkArhivskaOznakaExists(value))
      )
      .subscribe();
    this.itemViewProvider.item$
      .pipe(
        first(),
        tap((item) => {
          this.orderFormControl = this.formService.registerFormControl(
            'en~jedinica_gradiva~redoslijed',
            { value: null },
            Validators.required
          );
          this.value = (item as JedinicaGradiva)[
            'en~jedinica_gradiva~arhivska_oznaka'
          ];
        })
      )
      .subscribe();
  }

  override ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
  }

  override set value(value: string) {
    const preprocessedValue = this.preprocessValue(value);
    this.formControl.setValue(preprocessedValue);

    if (!(!!preprocessedValue && preprocessedValue.length > 0)) {
      this.prefixValue = '';
      if (this.orderFormControl) {
        this.orderFormControl.setValue(null);
      }
      return;
    }

    const indexOfLastDash = preprocessedValue.lastIndexOf('-');
    this.prefixValue = preprocessedValue.substring(0, indexOfLastDash + 1);
    const orderValue = preprocessedValue.substring(indexOfLastDash + 1);
    if (this.orderFormControl) {
      this.orderFormControl.setValue(Number(orderValue));
    }
  }

  override get value(): string {
    return this.formControl.value;
  }

  onOrderInput(event: InputNumberInputEvent) {
    const parentValue = this.prefixValue;
    if (event.value != undefined && event.value != null) {
      this.formControl.setValue(parentValue + event.value);
    } else {
      this.formControl.setValue(parentValue);
    }
  }

  private checkArhivskaOznakaExists(value: string) {
    return this.itemViewProvider.item$.pipe(
      first(),
      switchMap((item) => {
        const bi = new BusinessItem(item);
        const itemType = bi.typeName;
        const parentId = bi.id;

        const payload = {
          arhivskaOznaka: value,
          itemType: itemType,
          jedinicaGradivaId: parentId
        };

        const url = `${this.baseUrl}arhivska-oznaka/check-oznaka-exists`;
        return this.httpService
          .request<number>(HttpMethod.POST, url, payload)
          .pipe(
            switchMap((exists) => {
              if (exists == 1) {
                this.warnMessage = [
                  {
                    severity: 'warn',
                    detail:
                      'Upisali ste arhivsku oznaku koja se već nalazi u hijerarhiji, sve jedinice s postojećim redoslijedom će biti renumerirane!'
                  }
                ];
              } else if (exists > 1) {
                this.warnMessage = [
                  {
                    severity: 'error',
                    detail:
                      'U sustavu već postoje zapisi s navedenom arhivskom oznakom, izmijenite trenutne arhivske oznake na već postojećim zapisima.'
                  }
                ];
              } else if (exists == 0) {
                this.warnMessage = [];
              }
              return [];
            })
          );
      })
    );
  }
}
